<template>
  <div class="enter-email">
    <div class="login-card">
      <!-- Logo -->
      <div class="logo-wrapper">
        <img v-if="isDark" src="/images/logo-font-white.svg" alt="Yuzu" class="logo" />
        <img v-else src="/images/logo-font-black.svg" alt="Yuzu" class="logo" />
      </div>

      <!-- Heading -->
      <h2 v-if="isTalent" class="login-title">{{ $t('app.auth.enter-email.talent-heading') }}</h2>
      <h2 v-else class="login-title">{{ $t('app.auth.enter-email.org-heading') }}</h2>

      <!-- Email Input -->
      <div class="form-group">
        <label for="email" class="form-label">{{ $t( 'app.auth.enter-email.input-label') }}</label>
        <input
          id="email"
          v-model="email"
          type="email"
          class="form-input"
          placeholder="john@gmail.com"
          @keyup.enter="handleLogin"
        />
      </div>

      <!-- Continue Button -->
      <button
        class="btn-continue"
        :disabled="!email || isLoading"
        @click="handleLogin"
      >
        <span v-if="!isLoading">{{ $t('app.auth.enter-email.continue')}}</span>
        <span v-else>
          <i class="bi bi-arrow-repeat"></i>
        </span>
      </button>

      <!-- Terms Text -->
      <p class="terms-text">
        {{ $t('app.auth.terms.text') }}
        <a href="https://www.yuzu.hr/conditions-generales-d-utilisation">{{ $t('app.auth.terms.cgu') }}</a>{{ $t('app.auth.terms.text2') }}
        <a href="https://www.yuzu.hr/politique-de-confidentialite">{{ $t('app.auth.terms.privacy') }}</a>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { authApi } from "@/api/authentification.api";
import { useDark } from "@vueuse/core";
import { ref } from "vue";
import { useToast } from "vue-toastification";

const email = ref("");
const isLoading = ref(false);
const route = useRoute();
const isDark = useDark();
const toast = useToast();
const { t } = useI18n();

const emits = defineEmits(["submitEmail", "noAccount"]);

const handleLogin = async () => {
  if (!email.value || isLoading.value) return;

  const lowerCaseEmail = email.value.toLocaleLowerCase();

  isLoading.value = true;
  try {
    const invitationId = route.query.invitation_id as string;
    await authApi.requestCode(lowerCaseEmail, undefined, !!invitationId);
    emits("submitEmail", lowerCaseEmail);
  } catch (error) {
    const e = error as number;
    if (e === 404) {
      emits('noAccount', lowerCaseEmail);
    } else {
      toast.error(t('app.auth.enter-code.error'));
    }
  } finally {
    isLoading.value = false;
  }
};

const isTalent = computed(() => {
  return window.location.host.includes('game');
});

function goToOrganisationLogin() {
  // Get the current URL
  let currentURL = window.location.href;

  // Replace app with 'game'
  let modifiedURL = currentURL.replace("game", "app");

  return modifiedURL;
}

function goToTalentLogin() {
  // Get the current URL
  let currentURL = window.location.href;

  // Replace app with 'game'
  let modifiedURL = currentURL.replace("app", "game");

  // Redirect to the modified URL
  return modifiedURL;
}

onBeforeMount(async () => {
  const accountId = localStorage.getItem("accountId");
  if (accountId) {
    const invitationId = route.query.invitation_id as string;
    await authApi.requestCode(undefined, accountId, !!invitationId);
    emits("submitEmail", "");
  }
})
</script>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.enter-email {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 40%;
  max-width: 718px;

  @media screen and (max-width: $medium-screen) {
    width: 85%;
  }
}

.login-card {
  width: 100%;
  padding: 0;
}

.logo-wrapper {
  display: flex;
  margin-bottom: 2rem;

  @media screen and (max-width: $medium-screen) {
    justify-content: center;
    margin-bottom: 1rem;
  }
}

.logo {
  height: 32px;

  @media screen and (max-width: $medium-screen) {
    height: 25px;
  }
}

.login-title {
  margin-bottom: 2rem;
  font-size: 32px;
  font-weight: 600;

  color: var(--secondary-color);
  
  @media screen and (max-width: $medium-screen) {
    font-size: 20px !important;
    margin-bottom: 1rem;
    text-align: center;
    line-height: normal !important;
  }
}

.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 1.25rem;
}

.form-label {
  font-size: 16px;
  font-weight: 500;
  color: var(--secondary-color);
  margin-bottom: 0.5rem;

  @media screen and (max-width: $medium-screen) {
    font-size: 14px !important;
  }
}

.form-input {
  padding: 0.75rem 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 16px;
  font-family: inherit;
  color: var(--secondary-color);
  background-color: var(--white-color);
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: $primary-color;
    box-shadow: 0 0 0 3px rgba($primary-color, 0.1);
  }

  &::placeholder {
    color: #999;
  }

  @media screen and (max-width: $medium-screen) {
    font-size: 16px !important;
    padding: 0.7rem 0.5rem;
  }
}

.btn-continue {
  width: 100%;
  padding: 0.8rem;
  margin-bottom: 1.5rem;
  background-color: $primary-color;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 600;
  color: $secondary-color;
  cursor: pointer;
  transition: background-color 0.2s ease;

  @media screen and (max-width: $medium-screen) {
    font-size: 12px !important;
    padding: 0.7rem;
    margin-bottom: 1rem;
  }

  &:hover:not(:disabled) {
    background-color: darken($primary-color, 8%);
  }

  &:active:not(:disabled) {
    background-color: darken($primary-color, 16%);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  i {
    animation: spin 1s linear infinite;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.terms-text {
  font-size: 13px;
  color: #999;
  margin-bottom: 3rem;
  
  a {
    color: var(--secondary-color);
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  @media screen and (max-width: $medium-screen) {
    font-size: 9px !important;
    margin-bottom: 2rem;
    text-align: center;
  }
}

.org-link-wrapper {
  display: flex;
  justify-content: flex-start;

  @media screen and (max-width: $medium-screen) {
    justify-content: center;
  }
}

.org-link {
  font-size: 14px;
  color: var(--secondary-color);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;

  @media screen and (max-width: $medium-screen) {
    font-size: 10px !important;
    text-decoration: underline;
  }

  &:hover {
    color: $primary-color;
  }
}

@media screen and (max-width: $large-screen) {
  .login-container {
    min-height: auto;
    padding: 1.5rem;
  }

  .login-card {
    padding: 0;
  }

  .login-title {
    font-size: 32px;
  }
}
</style>
